import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowDown, Instagram, Youtube, Music2, Radio, HeartHandshake, Repeat, UserPlus, Heart, Users, PlayCircle } from 'lucide-react';
import api from '@/lib/api';
import Footer from '@/components/Footer';
import { MainHeader } from '@/components/main/MainHeader';
import { DetailMedia } from '@/pages/FeatureDetail';

const POINT_ICONS = [UserPlus, Heart, Users, PlayCircle];

const COMPARE = [
  ['주요 역할', '새로운 고객 유입', '단골 관계 형성'],
  ['고객 관계', '외부 플랫폼 중심', '판매자 브랜드 중심'],
  ['고객관리', '플랫폼별 기능 사용', 'LIVEMOA 고객관리 기능 활용'],
  ['방송 이후', '플랫폼별 콘텐츠 활용', '상품·영상 연계 판매 지향'],
  ['운영', '채널별 환경에 의존', '상품·주문·고객 통합 관리'],
];

const Step = ({ icon: Icon, title, children }) => (
  <div className="flex-1 min-w-0 rounded-2xl bg-white border border-[#E4E7F6] p-4 md:p-5 text-center">
    <Icon className="mx-auto w-8 h-8 text-[#2F5BFF]" />
    <p className="mt-2 text-[14px] md:text-[15px] xl:text-[17px] font-black text-[#1B2140] break-keep">{title}</p>
    <div className="mt-1.5 text-[12px] md:text-[13px] text-[#6B7194]">{children}</div>
  </div>
);

const Arrow = () => (
  <div className="flex items-center justify-center text-[#7B3DF5]">
    <ArrowRight className="hidden md:block w-6 h-6" /><ArrowDown className="md:hidden w-6 h-6" />
  </div>
);

// 실제 방송 화면 대신 고객 흐름 설명형 그래픽
const FlowGraphic = () => (
  <div className="rounded-[22px] bg-gradient-to-br from-[#EEF1FF] to-[#F3ECFF] p-4 md:p-6 flex flex-col md:flex-row gap-3 md:gap-2 items-stretch" data-testid="why-flow-graphic">
    <Step icon={Radio} title="외부 LIVE">
      <span className="inline-flex flex-wrap justify-center gap-x-2 gap-y-1">
        <span className="inline-flex items-center gap-1"><Music2 className="w-3.5 h-3.5" />TikTok</span>
        <span className="inline-flex items-center gap-1"><Instagram className="w-3.5 h-3.5" />Instagram</span>
        <span className="inline-flex items-center gap-1"><Youtube className="w-3.5 h-3.5" />YouTube</span>
      </span>
      <p className="mt-1">새로운 고객 유입</p>
    </Step>
    <Arrow />
    <Step icon={HeartHandshake} title="LIVEMOA 자사 LIVE">판매자 팔로우 · 단골 관계 형성</Step>
    <Arrow />
    <Step icon={Repeat} title="단골 · 재구매">다시 방문하고 다시 구매</Step>
  </div>
);

const StartBtn = ({ testid }) => (
  <Link to="/seller/apply" data-testid={testid}
    className="inline-flex items-center justify-center gap-2 h-14 px-7 rounded-2xl bg-gradient-to-r from-[#19B6FF] via-[#3D6BFF] to-[#8B3DFF] text-white text-[17px] font-bold shadow-[0_8px_24px_rgba(61,107,255,0.35)] hover:brightness-110 transition-[filter]">
    지금 무료로 시작하기 <ArrowRight className="w-5 h-5" />
  </Link>
);

const CompareTable = () => (
  <section className="px-4 md:px-12 py-10 md:py-16" data-testid="why-compare">
    <div className="max-w-[1100px] mx-auto">
      <h2 className="text-[22px] md:text-[34px] font-black tracking-tight text-[#1B2140]">외부 LIVE와 자사 LIVE, 함께 운영하세요</h2>
      <p className="mt-2 text-[14px] md:text-[16px] text-[#5B6185]">두 채널은 경쟁이 아니라 서로 다른 역할을 합니다.</p>
      <div className="mt-5 md:mt-8 rounded-2xl border border-[#E4E7F6] overflow-hidden">
        <div className="grid grid-cols-[72px_1fr_1fr] md:grid-cols-[160px_1fr_1fr] text-[12px] md:text-[16px] font-bold">
          <div className="bg-[#F6F7FF] p-2.5 md:p-4 text-[#6B7194]">구분</div>
          <div className="bg-[#F6F7FF] p-2.5 md:p-4 text-[#1B2140]">외부 플랫폼 LIVE</div>
          <div className="bg-gradient-to-r from-[#1E5BFF] to-[#6D3DF5] p-2.5 md:p-4 text-white">LIVEMOA 자사 LIVE</div>
        </div>
        {COMPARE.map(([k, a, b]) => (
          <div key={k} className="grid grid-cols-[72px_1fr_1fr] md:grid-cols-[160px_1fr_1fr] border-t border-[#E4E7F6] text-[12px] md:text-[16px] break-keep" data-testid="why-compare-row">
            <div className="p-2.5 md:p-4 font-bold text-[#6B7194]">{k}</div>
            <div className="p-2.5 md:p-4 text-[#1B2140]">{a}</div>
            <div className="p-2.5 md:p-4 font-semibold text-[#2F45E0] bg-[#F7F5FF]">{b}</div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export default function WhySelfLive({ user }) {
  const [page, setPage] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    api.get('/public/feature-pages/why-self-live').then((r) => setPage(r.data)).catch(() => setError(true));
  }, []);
  const hasMedia = page && (page.hero_image || page.hero_video);
  return (
    <div className="min-h-screen bg-white main-renewal-root" data-testid="why-self-live-page">
      <MainHeader user={user} />
      {error ? (
        <div className="px-6 py-24 text-center" data-testid="why-unavailable">
          <p className="text-[18px] font-bold text-[#1B2140]">준비 중인 페이지입니다.</p>
          <Link to="/" className="mt-4 inline-block text-[#2F5BFF] font-bold">메인으로 돌아가기</Link>
        </div>
      ) : !page ? <div className="h-[60vh]" /> : (
        <>
          <section className="bg-gradient-to-b from-[#F4F6FF] to-white px-4 md:px-12 pt-8 md:pt-14 pb-10 md:pb-16" data-testid="why-hero">
            <div className="max-w-[1340px] mx-auto grid md:grid-cols-[1fr_1.2fr] gap-6 md:gap-12 items-center">
              <div className="main-fade-up">
                <p className="text-[#2F45E0] font-bold text-[14px] md:text-[17px]">자사 LIVE가 필요한 이유</p>
                <h1 className="mt-2 text-[28px] md:text-[44px] font-black tracking-tight leading-[1.25] text-[#1B2140] whitespace-pre-line break-keep" data-testid="why-title">{page.title}</h1>
                <p className="mt-4 text-[14px] md:text-[17px] text-[#5B6185] leading-relaxed whitespace-pre-line break-keep" data-testid="why-intro">{page.intro}</p>
              </div>
              {hasMedia
                ? <DetailMedia image={page.hero_image} video={page.hero_video} className="aspect-[16/10]" testid="why-hero-media" />
                : <FlowGraphic />}
            </div>
          </section>
          <section className="px-4 md:px-12 py-10 md:py-16 bg-[#F6F7FF]" data-testid="why-points">
            <div className="max-w-[1340px] mx-auto grid grid-cols-1 min-[480px]:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-5">
              {page.items.map((it, i) => {
                const Icon = POINT_ICONS[i % POINT_ICONS.length];
                return (
                  <div key={it.key} className="rounded-2xl bg-white border border-[#E4E7F6] p-5 md:p-6" data-testid={`why-point-${it.key}`}>
                    {it.image ? <DetailMedia image={it.image} video={it.video} className="aspect-[16/10] mb-4" testid={`why-point-media-${it.key}`} />
                      : <span className="inline-flex w-11 h-11 rounded-xl items-center justify-center bg-gradient-to-br from-[#1E5BFF] to-[#6D3DF5] text-white"><Icon className="w-5 h-5" /></span>}
                    <h3 className="mt-3 text-[17px] md:text-[20px] font-black text-[#1B2140] break-keep">{it.name}</h3>
                    <p className="mt-2 text-[14px] md:text-[15px] text-[#5B6185] leading-relaxed break-keep">{it.desc}</p>
                  </div>
                );
              })}
            </div>
          </section>
          <CompareTable />
          <section className="px-4 md:px-12 pb-12 md:pb-20" data-testid="why-bottom-cta">
            <div className="max-w-[1340px] mx-auto rounded-3xl bg-gradient-to-r from-[#EEF2FF] via-[#F1EDFF] to-[#F7F3FF] border border-[#DCE2FF] px-6 md:px-14 py-10 md:py-14 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <h2 className="text-[22px] md:text-[34px] font-black tracking-tight text-[#1B2140] whitespace-pre-line break-keep" data-testid="why-bottom-title">{page.tagline}</h2>
              <StartBtn testid="why-start-btn" />
            </div>
          </section>
        </>
      )}
      <Footer user={user} />
    </div>
  );
}
